// videoPoster — a própria TV gera, via WebCodecs, o frame de SAÍDA de cada vídeo
// e cacheia em memória, pra cobrir a transição que SAI de um vídeo (mata o PRETO
// do vid→X em Tizen/webOS, onde `drawImage(<video>)` volta preto por HW overlay).
//
// DURATION OVERRIDE (Renato 2026-07-16): o frame de saída NÃO é sempre o último do
// arquivo. Quando o slot tem duration override, o player CORTA o vídeo no override
// → o frame que fica na tela é o do INSTANTE DO CORTE (`media.duration`), não o fim
// do arquivo. `decodeFrameAt(url, targetSec)` decodifica o GOP que CONTÉM esse
// instante e pega o frame de MAIOR timestamp ≤ target (paridade com o Android, que
// captura o frame VIVO no corte). Sem override → `targetSec=null` = último frame do
// arquivo (fim natural).
//
// PARIDADE COM O ANDROID (Renato 2026-07-16): o Android congela o ÚLTIMO frame do
// <video> que SAI (`lastFrameCache`, via drawImage). Aqui replicamos EXATAMENTE
// isso — o frame do vídeo que SAI, não o 1º do que entra. Vantagens:
//   (a) o canvas mostra o frame que JÁ estava na tela → ZERO preto no fim do vídeo;
//   (b) a troca vira canvas(último frame de A)→vídeo(1º frame de B) = corte de
//       CONTEÚDO → mascara a diferença de qualidade (canvas software × vídeo HW),
//       matando o "soco de resolução" que o 1º-frame-do-que-entra escancarava.
// A fonte no Tizen é WebCodecs (decode por SOFTWARE → escapa da parede do HW
// overlay); no Android/browser a fonte continua sendo `drawImage(<video>)` nativo.
//
// Provado na bancada (Tizen 9 / Chromium 120): fetch(cache url) → arrayBuffer →
// demux mp4box (último GOP + avcC/hvcC) → VideoDecoder → VideoFrame → canvas dá
// imagem NÃO-preta. Este módulo reusa a cadeia de demux compartilhada (funções
// abaixo, também usadas pelo `captureProbe.ts`).
//
// Escopo: SÓ deve ser alimentado em tizen/webos (o gate fica no chamador — Stream).
// Android/browser usam `lastFrameCache` (drawImage(<video>) funciona lá).
import type {
  MP4ArrayBuffer,
  MP4File,
  MP4Sample,
  MP4VideoTrack,
  DataStream as MP4DataStream,
} from 'mp4box';

/** Construtor da classe DataStream do mp4box (obtido em runtime via import()). */
export type MP4DataStreamCtor = typeof MP4DataStream;

/** Nº de amostras extraídas na busca pelo 1º sync sample (keyframe). O 1º sample
 *  de um MP4 normal já é sync; a folga cobre trilhas sem `stss` bem-comportado.
 *  Usado por `demuxFirstKeyframe` (compartilhado com o captureProbe). */
const KEYFRAME_SEARCH_SAMPLES = 60;

/** Timeout do decode ponta-a-ponta (fetch→demux→decode→bitmap). Decodar um GOP
 *  inteiro é mais pesado que 1 keyframe — timeout um pouco mais folgado. */
const DECODE_TIMEOUT_MS = 10000;

/** Fator de escala do frame guardado em memória. **1.0 = resolução CHEIA**
 *  (sem resize). O frame é exibido FULLSCREEN (1920×1080); um downscale deixava a
 *  imagem MOLE ao ser esticada. Full-res casa 1:1 com o `<video>` → nitidez
 *  máxima na transição (pedido do Renato).
 *
 *  Trade-off de memória: um ImageBitmap 1080p cru ocupa ~8MB (RGBA
 *  1920×1080×4) vs ~2MB a 0.5 — por isso o cap foi reduzido pra manter o teto de
 *  RAM na TV. Valores < 1 (economia de RAM opt-in) passam a usar
 *  `resizeQuality:'high'` pra não perder tanta nitidez. */
const FRAME_DOWNSCALE = 1.0;

/** Máximo de frames em memória. Com full-res, 6 × ~8MB (1080p RGBA) ≈ 48MB —
 *  teto ajustado à resolução cheia. Ao exceder, o mais antigo é fechado
 *  (`.close()`) e removido (LRU). Exportado pra o teste do cache não fixar o
 *  número. */
export const LAST_FRAME_WC_CACHE_CAP = 6;

/** Trunca mensagens de erro (log/diagnóstico). Compartilhado com o captureProbe. */
export function short(value: unknown): string {
  if (value instanceof Error) return value.message.slice(0, 28);
  return String(value ?? '').slice(0, 28);
}

/** Carrega o mp4box via import dinâmico (não pesa o bundle fora deste caminho).
 *  Compartilhado com o captureProbe. */
export async function loadMp4box(): Promise<{
  createFile: () => MP4File;
  DataStreamCtor: MP4DataStreamCtor;
}> {
  // mp4box: declarado em package.json (dependency). O import dinâmico mantém o
  // demuxer fora do bundle principal — só carrega quando um frame é gerado.
  // eslint-disable-next-line import/no-unresolved
  const mod = await import('mp4box');
  return {
    createFile: mod.createFile ?? mod.default.createFile,
    DataStreamCtor: mod.DataStream ?? mod.default.DataStream,
  };
}

/** Extrai a `description` (config record avcC/hvcC/…) que o VideoDecoder exige
 *  pra amostras AVCC/HVCC. Serializa o box e descarta os 8 bytes do header
 *  (size + fourCC), deixando só o payload do config. Retorna null se ausente.
 *  Compartilhado com o captureProbe. */
export function extractCodecDescription(
  file: MP4File,
  trackId: number,
  DataStreamCtor: MP4DataStreamCtor,
): Uint8Array | null {
  const trak = file.getTrackById(trackId);
  const entries = trak?.mdia?.minf?.stbl?.stsd?.entries ?? [];
  for (const entry of entries) {
    const box = entry.avcC ?? entry.hvcC ?? entry.vpcC ?? entry.av1C;
    if (box) {
      const stream = new DataStreamCtor(
        undefined,
        0,
        DataStreamCtor.BIG_ENDIAN,
      );
      box.write(stream);
      return new Uint8Array(stream.buffer, 8);
    }
  }
  return null;
}

export type DemuxResult =
  | { track: MP4VideoTrack; description: Uint8Array; keyData: Uint8Array }
  | { err: string };

/** Demuxa o MP4 (via mp4box) até o 1º keyframe da 1ª faixa de vídeo, coletando
 *  codec + description + os bytes do keyframe. Push-based (callbacks); resolve
 *  no 1º sync sample. `err` carrega a sub-etapa que falhou. **Compartilhado com o
 *  captureProbe** (por isso permanece exportado — o decode de LAST frame usa o
 *  `demuxLastGop` abaixo). */
export function demuxFirstKeyframe(
  buffer: ArrayBuffer,
  createFile: () => MP4File,
  DataStreamCtor: MP4DataStreamCtor,
): Promise<DemuxResult> {
  return new Promise<DemuxResult>((resolve) => {
    const file = createFile();
    let settled = false;
    let track: MP4VideoTrack | null = null;
    let description: Uint8Array | null = null;

    const finish = (result: DemuxResult): void => {
      if (settled) return;
      settled = true;
      try {
        file.stop();
      } catch {
        /* noop */
      }
      resolve(result);
    };

    file.onError = (e) => finish({ err: `demux:${short(e)}` });

    file.onReady = (info) => {
      track = info.videoTracks?.[0] ?? null;
      if (!track) {
        finish({ err: 'demux:no-video-track' });
        return;
      }
      description = extractCodecDescription(file, track.id, DataStreamCtor);
      if (!description) {
        finish({ err: 'demux:no-config-box' });
        return;
      }
      file.setExtractionOptions(track.id, null, {
        nbSamples: KEYFRAME_SEARCH_SAMPLES,
      });
      file.start();
    };

    file.onSamples = (_id, _user, samples) => {
      for (const sample of samples) {
        if (sample.is_sync && track && description) {
          finish({ track, description, keyData: sample.data });
          return;
        }
      }
    };

    try {
      const mp4buffer = buffer as MP4ArrayBuffer;
      mp4buffer.fileStart = 0;
      file.appendBuffer(mp4buffer);
      file.flush();
    } catch (e) {
      finish({ err: `demux:append:${short(e)}` });
    }
  });
}

export type LastGopResult =
  | { track: MP4VideoTrack; description: Uint8Array; samples: MP4Sample[] }
  | { err: string };

/** Meta mínima de amostra pra escolher o GOP — testável sem mp4box/WebCodecs. */
export interface GopSampleMeta {
  is_sync: boolean;
  cts: number;
  timescale: number;
}

/** Tempo de apresentação da amostra em segundos (cts/timescale). timescale 0 →
 *  cts cru (ordenação relativa preservada; fallback pra faixas sem timescale). */
export function sampleCtsSeconds(sample: GopSampleMeta): number {
  return sample.timescale ? sample.cts / sample.timescale : sample.cts;
}

/**
 * Escolhe as amostras do GOP relevante de uma faixa de vídeo (amostras em ordem de
 * DECODE). ESPEC PURA do critério — o `demuxGopAt` abaixo implementa o MESMO
 * critério de forma incremental (streaming, memória de 1 GOP). Testado em
 * `videoPoster.test.ts`.
 *
 *  - `targetSec == null` → ÚLTIMO GOP (do último keyframe até EOF) = "último frame
 *    do arquivo" (comportamento histórico do `decodeLastFrame`).
 *  - `targetSec >= 0` → GOP que CONTÉM esse instante: começa no keyframe cujo cts
 *    ≤ target e cujo PRÓXIMO keyframe passa do target. Necessário porque com
 *    duration override o vídeo é CORTADO no instante do override → o frame que fica
 *    na tela é o do corte, não o fim do arquivo.
 *
 * **Clamp implícito à duração do arquivo:** se `target ≥ duração real` (nenhum
 * keyframe posterior ao target), cai no ÚLTIMO GOP — igual a `null`. Assim não é
 * preciso ler `duration/timescale` da faixa: a ausência de keyframe > target já
 * significa "target além do arquivo".
 *
 * Retorna `[]` se o GOP escolhido não começar num keyframe (arquivo inválido) →
 * o chamador degrada gracioso (frame preto 1× na 1ª exibição).
 */
export function selectGopForTarget<T extends GopSampleMeta>(
  samples: T[],
  targetSec: number | null,
): T[] {
  let gop: T[] = [];
  let gopStartSec = 0;

  for (const sample of samples) {
    if (sample.is_sync) {
      const kfSec = sampleCtsSeconds(sample);
      // O GOP anterior (que começou ≤ target) CONTÉM o target quando o novo
      // keyframe já passou do target → é o escolhido.
      if (
        targetSec != null &&
        gop.length > 0 &&
        gopStartSec <= targetSec &&
        kfSec > targetSec
      ) {
        return gop;
      }
      // Janela rolante: recomeça o acumulador no novo keyframe (memória de 1 GOP).
      gop = [];
      gopStartSec = kfSec;
    }
    gop.push(sample);
  }
  // EOF: último GOP acumulado. Cobre `null` (último frame do arquivo) E
  // `target` no último GOP / target ≥ duração (clamp implícito).
  if (gop.length === 0 || !gop[0].is_sync) return [];
  return gop;
}

/**
 * Demuxa o MP4 (via mp4box) e coleta as amostras do GOP relevante da 1ª faixa de
 * vídeo. Necessário porque o frame EXIBIDO não é decodável isoladamente (frames
 * P/B dependem do keyframe do seu GOP + dos frames anteriores em ordem de decode).
 *
 * `targetSec`:
 *  - `null` → ÚLTIMO GOP (último frame do arquivo).
 *  - número → GOP que CONTÉM esse instante (corte por duration override), com
 *    clamp implícito pro último GOP se `target ≥ duração`. Mesmo critério do
 *    `selectGopForTarget` (spec pura), aqui aplicado incrementalmente pra manter
 *    a memória em 1 GOP (não segura o vídeo inteiro).
 */
export function demuxGopAt(
  buffer: ArrayBuffer,
  createFile: () => MP4File,
  DataStreamCtor: MP4DataStreamCtor,
  targetSec: number | null,
): Promise<LastGopResult> {
  return new Promise<LastGopResult>((resolve) => {
    const file = createFile();
    let settled = false;
    let track: MP4VideoTrack | null = null;
    let description: Uint8Array | null = null;
    let total = 0;
    let received = 0;
    let gop: MP4Sample[] = [];
    let gopStartSec = 0;

    const finish = (result: LastGopResult): void => {
      if (settled) return;
      settled = true;
      try {
        file.stop();
      } catch {
        /* noop */
      }
      resolve(result);
    };

    file.onError = (e) => finish({ err: `demux:${short(e)}` });

    file.onReady = (info) => {
      track = info.videoTracks?.[0] ?? null;
      if (!track) {
        finish({ err: 'demux:no-video-track' });
        return;
      }
      description = extractCodecDescription(file, track.id, DataStreamCtor);
      if (!description) {
        finish({ err: 'demux:no-config-box' });
        return;
      }
      total = track.nb_samples || 0;
      if (total <= 0) {
        finish({ err: 'demux:no-samples' });
        return;
      }
      // nbSamples = total → mp4box entrega tudo (em 1+ batches) numa passada.
      file.setExtractionOptions(track.id, null, { nbSamples: total });
      file.start();
    };

    file.onSamples = (_id, _user, samples) => {
      for (const sample of samples) {
        if (sample.is_sync) {
          const kfSec = sampleCtsSeconds(sample);
          // GOP anterior contém o target → pronto (mesmo critério de selectGopForTarget).
          if (
            targetSec != null &&
            gop.length > 0 &&
            gopStartSec <= targetSec &&
            kfSec > targetSec &&
            track &&
            description
          ) {
            finish({ track, description, samples: gop });
            return;
          }
          // Janela rolante: recomeça no novo keyframe (memória de 1 GOP).
          gop = [];
          gopStartSec = kfSec;
        }
        gop.push(sample);
        received++;
      }
      if (received >= total && track && description) {
        // EOF: último GOP (null OU target no último GOP OU clamp target ≥ duração).
        if (gop.length === 0 || !gop[0].is_sync) {
          finish({ err: 'demux:no-sync-in-tail' });
          return;
        }
        finish({ track, description, samples: gop });
      }
    };

    try {
      const mp4buffer = buffer as MP4ArrayBuffer;
      mp4buffer.fileStart = 0;
      file.appendBuffer(mp4buffer);
      file.flush();
    } catch (e) {
      finish({ err: `demux:append:${short(e)}` });
    }
  });
}

/** Compat: demuxa o ÚLTIMO GOP (= `demuxGopAt(..., null)`). Mantido exportado
 *  pra referência de teste; internamente o decode usa `demuxGopAt`. */
export function demuxLastGop(
  buffer: ArrayBuffer,
  createFile: () => MP4File,
  DataStreamCtor: MP4DataStreamCtor,
): Promise<LastGopResult> {
  return demuxGopAt(buffer, createFile, DataStreamCtor, null);
}

/** VideoFrame → ImageBitmap. Fecha o frame sempre.
 *  - `FRAME_DOWNSCALE >= 1` (default 1.0) → **resolução CHEIA**, sem opções de
 *    resize: o bitmap sai idêntico ao frame decodado → nitidez máxima fullscreen.
 *  - `FRAME_DOWNSCALE < 1` (economia de RAM opt-in) → resize com
 *    `resizeQuality:'high'` (Lanczos-like) pra minimizar a perda de nitidez.
 *  Fallback pra full-res se o firmware não aceitar as opções de resize.
 *  O bitmap sai em `displayWidth × displayHeight` (mesmas dims do `<video>`) →
 *  casa 1:1 com o object-fit do player, sem "soco" de escala/crop. */
async function frameToBitmap(frame: VideoFrame): Promise<ImageBitmap> {
  const w = frame.displayWidth || frame.codedWidth || 0;
  const h = frame.displayHeight || frame.codedHeight || 0;
  try {
    if (w > 0 && h > 0 && FRAME_DOWNSCALE < 1) {
      return await createImageBitmap(frame, {
        resizeWidth: Math.max(1, Math.round(w * FRAME_DOWNSCALE)),
        resizeHeight: Math.max(1, Math.round(h * FRAME_DOWNSCALE)),
        resizeQuality: 'high',
      });
    }
    // full-res (sem resize) = mais nítido. colorSpaceConversion:'none' preserva o
    // color space nativo do VideoFrame (HW) — evita a conversão pra sRGB que pode
    // "lavar"/amolecer a cor vs o <video> ao vivo. (Borrado residual em painel 4K
    // com conteúdo 1080p é limite físico: poster 1080p upscaled pelo compositor vs
    // HW scaler do vídeo — não há mais detalhe que o 1080p da fonte.)
    return await createImageBitmap(frame, { colorSpaceConversion: 'none' });
  } catch {
    return createImageBitmap(frame); // fallback full-res (sem resize options)
  } finally {
    try {
      frame.close();
    } catch {
      /* noop */
    }
  }
}

/** Tolerância (µs) ao filtrar frames por `targetSec`: ~1ms absorve o
 *  arredondamento cts↔µs (frame exatamente no instante do corte deve entrar) sem
 *  alcançar o PRÓXIMO frame (espaçados ≥ ~16ms mesmo a 60fps). */
const TARGET_TS_TOLERANCE_US = 1000;

/**
 * DECODE de um frame de um vídeo → ImageBitmap (full-res). Ponta-a-ponta:
 * fetch(url) → demux mp4box (GOP relevante + avcC/hvcC) → VideoDecoder decode de
 * TODO o GOP em ordem → flush → pega o VideoFrame de MAIOR timestamp ≤ target (o
 * último EXIBIDO; robusto a reordenação por frames B) → ImageBitmap. Retorna null
 * em QUALQUER falha (codec não suportado, fetch, timeout, decode) — o chamador
 * degrada gracioso (transição fica preta 1× na 1ª exibição).
 *
 * `targetSec`:
 *  - `null` → último frame do ARQUIVO (fim natural — vídeo sem override).
 *  - número → frame EXIBIDO no instante do corte (duration override). O vídeo é
 *    cortado na duração efetiva → o frame que fica na tela é o do corte, NÃO o fim
 *    do arquivo. Paridade com o Android (que captura o frame VIVO no corte via
 *    `currentTime` vs `scheduledDuration`). Clamp: `target ≥ duração do arquivo` →
 *    cai no último GOP (o `selectGopForTarget`/`demuxGopAt` já resolve isso).
 *
 * CUSTO: decodar um GOP inteiro é mais pesado que 1 keyframe. Por isso o chamador
 * (`ensureLastFrameWc`) mantém a geração SERIALIZADA + com throttle, pra NÃO
 * contender com o decoder de reprodução. Fecha VideoFrame(s) e VideoDecoder sempre.
 */
export async function decodeFrameAt(
  url: string,
  targetSec: number | null,
): Promise<ImageBitmap | null> {
  if (typeof VideoDecoder === 'undefined') return null;

  // Teto de timestamp (µs) pro frame escolhido. null → sem teto (último frame).
  // Número → instante do corte + tolerância (não pega o frame pós-corte).
  const maxTsUs =
    targetSec != null
      ? Math.round(targetSec * 1e6) + TARGET_TS_TOLERANCE_US
      : Infinity;

  let decoder: VideoDecoder | null = null;
  let timedOut = false;

  const closeDecoder = (): void => {
    try {
      decoder?.close();
    } catch {
      /* noop */
    }
    decoder = null;
  };

  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      timedOut = true;
      closeDecoder();
      resolve(null);
    }, DECODE_TIMEOUT_MS);
  });

  const work = async (): Promise<ImageBitmap | null> => {
    // 1) Bytes do arquivo (cache local ou remoto). fetch de cache url funciona no
    //    WebView Tizen (provado pelo captureProbe).
    let buffer: ArrayBuffer;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        // Drena antes de sair: o corpo do erro tem bytes de verdade e largar a
        // `Response` sem ler nem cancelar segura o data pipe do Chromium
        // (~2 MB de address space por requisição no renderer 32-bit, que nunca
        // voltam — causa-raiz do renderer-kill do Tab A7, commit 683464d).
        await response.body?.cancel().catch(() => undefined);
        return null;
      }
      buffer = await response.arrayBuffer();
    } catch {
      return null;
    }
    if (timedOut) return null;

    // 2) Demux até o GOP relevante (último GOP, ou o que contém o instante do corte).
    let createFile: () => MP4File;
    let DataStreamCtor: MP4DataStreamCtor;
    try {
      ({ createFile, DataStreamCtor } = await loadMp4box());
    } catch {
      return null;
    }
    if (timedOut) return null;

    const demux = await demuxGopAt(
      buffer,
      createFile,
      DataStreamCtor,
      targetSec,
    );
    if ('err' in demux) return null;
    if (timedOut) return null;

    const { track, description, samples } = demux;
    const width = track.video?.width || track.track_width || 0;
    const height = track.video?.height || track.track_height || 0;
    const config: VideoDecoderConfig = {
      codec: track.codec,
      description,
      codedWidth: width || undefined,
      codedHeight: height || undefined,
    };

    // 3) Confirma suporte (unsupported = TV não decoda esse codec por SW).
    try {
      const support = await VideoDecoder.isConfigSupported(config);
      if (!support.supported) return null;
    } catch {
      return null;
    }
    if (timedOut) return null;

    // 4) Decode do GOP inteiro em ordem → flush → pega o frame de MAIOR timestamp
    //    ≤ maxTsUs (o último EXIBIDO até o corte). O keyframe do GOP tem cts ≤
    //    target por construção → `best` nunca fica null por causa do teto.
    return new Promise<ImageBitmap | null>((resolve) => {
      let settled = false;
      // Guarda só o frame de maior timestamp ≤ teto (último EXIBIDO); fecha os
      // demais → no máx. 1-2 VideoFrames abertos por vez (memória limitada).
      let best: VideoFrame | null = null;
      let bestTs = -Infinity;

      const fail = (): void => {
        if (settled) return;
        settled = true;
        closeDecoder();
        try {
          best?.close();
        } catch {
          /* noop */
        }
        best = null;
        resolve(null);
      };

      const finalize = (): void => {
        if (settled) return;
        settled = true;
        closeDecoder();
        const frame = best;
        best = null;
        // Timeout já venceu a corrida (outer resolveu null): não gasta CPU num
        // bitmap que ninguém usa — só fecha o frame e sai.
        if (!frame || timedOut) {
          try {
            frame?.close();
          } catch {
            /* noop */
          }
          resolve(null);
          return;
        }
        // frameToBitmap fecha o frame no finally.
        frameToBitmap(frame)
          .then((bitmap) => resolve(bitmap))
          .catch(() => resolve(null));
      };

      try {
        decoder = new VideoDecoder({
          output: (frame) => {
            // Ignora frames APÓS o corte (timestamp > teto): nunca foram exibidos.
            if (
              !timedOut &&
              frame.timestamp > bestTs &&
              frame.timestamp <= maxTsUs
            ) {
              const prev = best;
              best = frame;
              bestTs = frame.timestamp;
              if (prev) {
                try {
                  prev.close();
                } catch {
                  /* noop */
                }
              }
            } else {
              try {
                frame.close();
              } catch {
                /* noop */
              }
            }
          },
          error: () => fail(),
        });
        decoder.configure(config);
        for (const sample of samples) {
          const ts = sample.timescale
            ? Math.round((sample.cts / sample.timescale) * 1e6)
            : sample.cts; // fallback: cts cru (ordenação relativa preservada)
          decoder.decode(
            new EncodedVideoChunk({
              type: sample.is_sync ? 'key' : 'delta',
              timestamp: ts,
              data: sample.data,
            }),
          );
        }
        void decoder
          .flush()
          .then(() => finalize())
          .catch(() => finalize());
      } catch {
        fail();
      }
    });
  };

  return Promise.race([work(), timeoutPromise]);
}

/** Compat: decodifica o ÚLTIMO frame do arquivo (= `decodeFrameAt(url, null)`). */
export function decodeLastFrame(url: string): Promise<ImageBitmap | null> {
  return decodeFrameAt(url, null);
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache LRU em memória (contentId → ImageBitmap do ÚLTIMO frame). Escopo de
// sessão (some no restart do app → re-gera na 1ª exibição pós-boot). Cap
// LAST_FRAME_WC_CACHE_CAP; evictados são fechados (`.close()`) pra não vazar RAM
// na TV. "Wc" = WebCodecs (distingue do `lastFrameCache` nativo do Android).
// ─────────────────────────────────────────────────────────────────────────────
const lastFrameCacheWc = new Map<string, ImageBitmap>();

function closeBitmap(bitmap: ImageBitmap | undefined): void {
  try {
    bitmap?.close();
  } catch {
    /* noop */
  }
}

export const lastFrameWcCache = {
  has(id: string): boolean {
    return lastFrameCacheWc.has(id);
  },
  get(id: string): ImageBitmap | null {
    const bitmap = lastFrameCacheWc.get(id);
    if (!bitmap) return null;
    // Toque LRU: move pro fim (mais-recentemente-usado).
    lastFrameCacheWc.delete(id);
    lastFrameCacheWc.set(id, bitmap);
    return bitmap;
  },
  set(id: string, bitmap: ImageBitmap): void {
    const existing = lastFrameCacheWc.get(id);
    if (existing && existing !== bitmap) closeBitmap(existing);
    lastFrameCacheWc.delete(id);
    lastFrameCacheWc.set(id, bitmap);
    // Evict LRU: fecha os mais antigos até caber no cap.
    while (lastFrameCacheWc.size > LAST_FRAME_WC_CACHE_CAP) {
      const oldestKey = lastFrameCacheWc.keys().next().value as
        | string
        | undefined;
      if (oldestKey === undefined) break;
      closeBitmap(lastFrameCacheWc.get(oldestKey));
      lastFrameCacheWc.delete(oldestKey);
    }
  },
  delete(id: string): void {
    closeBitmap(lastFrameCacheWc.get(id));
    lastFrameCacheWc.delete(id);
  },
  size(): number {
    return lastFrameCacheWc.size;
  },
  /** Esvazia o cache inteiro, fechando todos os bitmaps. Útil em testes/cleanup. */
  clear(): void {
    lastFrameCacheWc.forEach((bitmap) => closeBitmap(bitmap));
    lastFrameCacheWc.clear();
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ensureLastFrameWc — gera o frame de saída de um vídeo (se ainda não existe) de
// forma SERIALIZADA (1 decode por vez) pra não contender com o decoder de
// reprodução, com THROTTLE entre decodes (GOP é pesado). Idempotente: no-op se
// já cacheado ou já em voo. NÃO bloqueia a reprodução (async). O gate tizen/webos
// fica no chamador (Stream).
//
// `targetSec` = duração EFETIVA do slot (instante do corte): `media.duration`
// quando há duration override, `null` quando é a duração natural (fim do arquivo).
// Sem override, decodifica o último frame do arquivo (comportamento histórico).
// ─────────────────────────────────────────────────────────────────────────────
const inFlight = new Set<string>();
let decodeChain: Promise<void> = Promise.resolve();

/** Gap entre decodes consecutivos na fila — dá "ar" pra CPU entre GOPs pesados,
 *  evitando pico sustentado que competiria com o decoder de reprodução na TV. */
const THROTTLE_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function ensureLastFrameWc(
  contentId: string,
  url: string,
  targetSec: number | null = null,
): void {
  if (lastFrameWcCache.has(contentId) || inFlight.has(contentId)) return;
  inFlight.add(contentId);
  decodeChain = decodeChain
    .then(async () => {
      // Re-checa: pode ter sido gerado enquanto esperava na fila.
      if (lastFrameWcCache.has(contentId)) return;
      const bitmap = await decodeFrameAt(url, targetSec);
      if (bitmap) lastFrameWcCache.set(contentId, bitmap);
      // Throttle DEPOIS do decode: segura a próxima da fila, não a atual.
      await delay(THROTTLE_MS);
    })
    .catch(() => {
      /* falha de decode → sem frame; degrada gracioso (preto 1× na 1ª exibição) */
    })
    .finally(() => {
      inFlight.delete(contentId);
    });
}
